/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Simon Paidla <s.paidla@techdivision.com>
 */

import { useFormikContext } from 'formik';
import { useQuery } from '../../../../Hooks/API/useQuery';
import { useCheckoutStore } from '../../../../Hooks/Zustand/useCheckoutStore';

/**
 * Custom StudioForm hook
 * @returns {{isLoading, studioData}}
 */
export function useStudioForm() {
  const {
    data,
    isLoading,
  } = useQuery('studios');
  const { setFieldValue, values } = useFormikContext();
  const { updateFormDataValue } = useCheckoutStore();

  /**
   * Remove selected rate bundle when user changed selected studio
   */
  const removeSelectedRateBundleIfAvailable = () => {
    setFieldValue('rateBundle', null);
    setFieldValue('rateBundleTerm', null);
  };

  /**
   * Return studio data by id
   * @param id
   */
  const getStudioById = (id) => data.find((studio) => studio.id === id);

  /**
   * Set studio data to formik context
   *
   * @param studioData
   */
  const setStudioToFormikContext = (studioData) => {
    updateFormDataValue('studio', studioData);
    setFieldValue('studio', studioData);
    setFieldValue('studioId', studioData.id);
    removeSelectedRateBundleIfAvailable();
  };

  /**
   * Map the studio data for the selection
   *
   * @param studioData
   * @returns {*}
   */
  const getMappedStudioData = (studioData) => {
    const filteredStudioData = studioData.filter((studio) => {
      // Headquarter studios should not be selectable
      const studioIsHeadquarter = studio.studioTags.some((studioTag) => studioTag.name === 'HQ');
      return studio.hasRateBundles && !studioIsHeadquarter;
    });

    return filteredStudioData.map((studio) => ({
      label: studio.studioName,
      value: studio.id,
    }));
  };

  return {
    isLoading,
    studioData: data,
    getStudioById,
    setStudioToFormikContext,
    getMappedStudioData,
    currentStudioId: values.studioId,
  };
}
